import moment from 'moment-timezone';
import { ProfileReservation, ReservationKind } from './getReservationActions';
import { resolveZone } from './formatBookings';

/* Ported from office (utils/checkIn.ts, domain/desks/getDeskReservationCheckInStart.ts) */

const SEAT_RESERVATION_CHECK_IN_WINDOW = 'seat-reservation-check-in-window';

const DEFAULT_WORKING_HOUR_START = 7;

type TimeFrame = { start?: string | null; end?: string | null };

const hoursFromTimeFrame = (
  timeFrame: TimeFrame | undefined
): number | null => {
  if (!timeFrame?.start) {
    return null;
  }
  return moment.duration(timeFrame.start).asHours();
};

const getWorkingHourStartForDay = (
  workingHours: ProfileReservation['seat']['location']['workingHours'],
  day: number
): number => {
  const workingHour = workingHours?.find((wh) => wh?.day === day);
  const start = hoursFromTimeFrame(workingHour?.timeFrames?.[0] ?? undefined);
  return start ?? DEFAULT_WORKING_HOUR_START;
};

const getCustomWorkingHourStartForDate = (
  customWorkingHours: ProfileReservation['seat']['location']['customWorkingHours'],
  date: string
): number | null => {
  const custom = customWorkingHours?.find((cwh) => cwh?.date === date);
  return hoursFromTimeFrame(custom?.timeFrames?.[0] ?? undefined);
};

const getReservationStartForCheckIn = (
  reservation: ProfileReservation,
  kind: ReservationKind
): moment.Moment | null => {
  const zone = resolveZone(
    reservation.seat.location.timezone ?? reservation.timeZone
  );
  const reservationStart = moment.tz(reservation.startTime, zone);
  if (!reservationStart.isValid()) {
    return null;
  }

  const { workingHours, customWorkingHours } = reservation.seat.location;
  if (kind !== 'assignment' || !workingHours) {
    return reservationStart;
  }

  const reservationDate = reservationStart.format('YYYY-MM-DD');
  const startHourFractional =
    getCustomWorkingHourStartForDate(customWorkingHours, reservationDate) ??
    getWorkingHourStartForDay(workingHours, reservationStart.day());

  const startHour = Math.floor(startHourFractional);
  const startMinute = Math.round((startHourFractional - startHour) * 60);

  return reservationStart
    .clone()
    .startOf('day')
    .hour(startHour)
    .minute(startMinute)
    .second(0);
};

const parseDuration = (
  value: string | null | undefined
): moment.Duration | null => {
  if (!value) {
    return null;
  }
  const parsed = moment.duration(value);
  return parsed.isValid() ? parsed : null;
};

export const getCheckInStart = (
  reservation: ProfileReservation,
  kind: ReservationKind
): moment.Moment | null => {
  const reservationStart = getReservationStartForCheckIn(reservation, kind);
  if (!reservationStart) {
    return null;
  }

  const seatWindow = parseDuration(
    reservation.seat.settings?.find(
      (setting) => setting?.slug === SEAT_RESERVATION_CHECK_IN_WINDOW
    )?.value
  );
  if (seatWindow) {
    return reservationStart.clone().subtract(seatWindow);
  }

  const policyWindow = parseDuration(
    reservation.seat.reservationPolicies?.checkinPolicies?.checkInWindow
  );
  if (policyWindow) {
    return reservationStart.clone().subtract(policyWindow);
  }

  return reservationStart;
};
